import {QuoteFeedService} from "../../../../components/chartiq/services/QuoteFeedService";
import {IHttpService} from "angular";
import {ChartIQDataPoint} from "../../../../components/chartiq/types/ChartIQFeed";
import "rxjs/add/operator/toPromise";
import {Simulation} from "../../../models/Simulation";
import {UserConfig} from "../../../../types/UserConfig";
import {LocalStorageService} from "../../../../types/NgStorage";
import {Optional} from "../../../../js/util/Optional";

import {StringKeyed} from "../../../../types/GenericTypes";
import {TfChartDataDailySeries, TfChartDataSeries} from "../../../../components/chartiq/models/TfChartData";

export interface StockChartSettings {
    layout: any;
    drawings: any;
}

export class StockChartService extends QuoteFeedService {

    public static id = "stockChartService";

    private readonly simulationChartStorage: StringKeyed<StockChartSettings>;
    private readonly simulationChartStorageKey: string;
    private readonly codeChartStorage: StringKeyed<StockChartSettings>;
    private readonly codeChartStorageKey: string;

    /** @ngInject */
    public constructor($http: IHttpService,
                       private $localStorage: LocalStorageService,
                       userConfig: UserConfig) {
        super($http);

        const userId = userConfig.id;
        this.simulationChartStorageKey = `simulation-chart-${userId}`;
        this.$localStorage.$default({[this.simulationChartStorageKey]: {}});
        this.simulationChartStorage = this.$localStorage[this.simulationChartStorageKey];

        this.codeChartStorageKey = `simulation-chart-code-${userId}`;
        this.$localStorage.$default({[this.codeChartStorageKey]: {}});
        this.codeChartStorage = this.$localStorage[this.codeChartStorageKey];
    }

    public async request(code: string, startDate: Date, endDate: Date): Promise<TfChartDataDailySeries> {
        return this.retrieveData(code, startDate, endDate, null, null);
    }

    public removeOrphanedSettings(simulations: Array<Simulation>): void {
        const newKeys = simulations.map(s => String(s.id));
        const savedKeys = Object.keys(this.simulationChartStorage);

        const keysToDelete = savedKeys.filter(k => !newKeys.includes(k));

        for (const key of keysToDelete) {
            delete this.simulationChartStorage[key];
        }

        this.$localStorage[this.simulationChartStorageKey] = this.simulationChartStorage;

    }

    private getSettingsForCode(code: string): StockChartSettings {
        return Optional.of(this.codeChartStorage)
            .map(d => d[code])
            .orElse({
                layout: null,
                drawings: null
            });
    }

    private setSettingsForCode(code: string, settings: StockChartSettings) {
        this.codeChartStorage[code] = settings;
        this.$localStorage[this.codeChartStorageKey] = this.codeChartStorage;
        this.$localStorage.$apply();
    }

    private getSettingsForSimulation(simulationId: number): StockChartSettings {
        return Optional.of(this.simulationChartStorage)
            .map(d => d[String(simulationId)])
            .orElse({
                layout: null,
                drawings: null
            });
    }

    private setSettingsForSimulation(simulationId: number, settings: StockChartSettings) {
        this.simulationChartStorage[String(simulationId)] = settings;
        this.$localStorage[this.simulationChartStorageKey] = this.simulationChartStorage;
        this.$localStorage.$apply();
    }

    public getLayoutFor(code: string): any {
        return this.getSettingsForCode(code).layout;
    }

    public setLayoutFor(code: string, layoutData: any) {
        const settings = this.getSettingsForCode(code);
        settings.layout = layoutData;
        this.setSettingsForCode(code, settings);
    }

    public getDrawingsFor(simulationId: number) {
        return this.getSettingsForSimulation(simulationId).drawings;
    }

    public setDrawingsFor(simulationId: number, drawingData: any) {
        const settings = this.getSettingsForSimulation(simulationId);
        settings.drawings = drawingData;
        this.setSettingsForSimulation(simulationId, settings);
    }
}
